import React from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Divider, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Chip, IconButton, Button, Breadcrumbs, Link
} from '@mui/material';
import { 
    ArrowBack, Receipt, Person, Store, LocationOn, 
    Payment, AccessTime, Info 
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const ViewBookingDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const booking = location.state?.booking;

    if (!booking) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="error">Booking data not found</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBack />} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'delivered') return 'success';
        if (s === 'cancelled' || s === 'rejected') return 'error';
        if (s === 'pending' || s === 'processing') return 'warning';
        return 'default';
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/partners'); }}>
                        Partners
                    </Link>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }}>
                        Bookings List
                    </Link>
                    <Typography color="text.primary">Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            Booking #{booking.bookingNumber}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                            <Chip 
                                label={booking.status?.toUpperCase()} 
                                color={getStatusColor(booking.status)} 
                                size="small" 
                                sx={{ fontWeight: 900, borderRadius: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 16 }} />
                                Placed on: {new Date(booking.createdAt).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column: People & Places */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Person color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Customer Details</Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{booking.user?.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{booking.user?.mobile}</Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <LocationOn sx={{ color: 'text.secondary', mt: 0.5 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Delivery Address</Typography>
                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                                        {booking.address?.addressType || 'Home'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {booking.address?.street}, {booking.address?.area}<br />
                                        {booking.address?.city}, {booking.address?.state} - {booking.address?.pincode}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Store color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Vendor Details</Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{booking.foodVendor?.shopName}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{booking.foodVendor?.mobile}</Typography>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Vendor Location</Typography>
                            <Typography variant="body2">
                                {booking.foodVendor?.addresses?.[0]?.addressLine1}<br />
                                {booking.foodVendor?.addresses?.[0]?.addressLine2}<br />
                                {booking.foodVendor?.addresses?.[0]?.city}, {booking.foodVendor?.addresses?.[0]?.state}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Order Items & Finances */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Receipt color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Order Items</Typography>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Item Description</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {booking.items?.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.menuItemName}</Typography>
                                                    <Typography variant="caption" color="text.secondary">Variant: {item.menuItemVariant}</Typography>
                                                </TableCell>
                                                <TableCell align="center">{item.quantity}x</TableCell>
                                                <TableCell align="right">₹{item.unitPrice}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>₹{item.totalPrice}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Payment color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Bill Summary</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Items Total</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.itemsTotal}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">GST Amount</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.gstAmount}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Delivery Charges</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.deliveryCharge}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Service Charges</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{booking.serviceCharge}</Typography>
                                </Box>
                                
                                <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Grand Total</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>₹{booking.grandTotal}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Info color="action" />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Status</Typography>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 900, color: booking.paymentStatus?.toLowerCase() === 'paid' ? 'success.main' : 'warning.main' }}>
                                            {booking.paymentStatus || 'Pending'}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Payment color="action" />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Method</Typography>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 900, color: booking.paymentMethod?.toLowerCase() === 'online' ? 'info.main' : booking.paymentMethod?.toLowerCase() === 'cod' ? 'warning.main' : 'text.secondary' }}>
                                            {booking.paymentMethod || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewBookingDetails;
